import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookOpen, User, MapPin, FileEdit, Target, TrendingUp, ArrowRight, PlayCircle, PenTool, Lock, Notebook, Lightbulb, Video, Archive } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { toast } from 'sonner';
import AuthModal from '../components/auth/AuthModal';
import CommunityBanner from '../components/common/CommunityBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const [stats, setStats] = useState(null);
  const [transparencyStats, setTransparencyStats] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  
  // Module access enforcement
  const { 
    isOralActive, isWrittenActive, getOralStatus, getWrittenStatus,
    isPracticeSessionsActive, getPracticeSessionsStatus,
    isProgressTrackerActive, getProgressTrackerStatus,
    isNotebookActiveForGrade, getNotebookStatusForGrade,
    isInsightsActiveForGrade, getInsightsStatusForGrade,
    getLiveClassesStatusForGrade,
    isModuleHidden,
    isModuleVisible,
    getModuleStatus
  } = useModuleAccess();

  // Paid subscription access (plan module_access flags)
  const { canAccessLiveClasses } = useSubscriptionAccess();

  // Check module access based on user's selected grade
  const userGrade = preferences?.grade;
  const isLoggedIn = isAuthenticated();

  // ── Live Classes card visibility state ────────────────────────────────
  // Grade-wise rollout: read `live_classes_<grade>` first, fall back to
  // the parent row. States:
  //   • hidden      → don't render the card at all (module off for grade)
  //   • coming_soon → show card with amber "Coming Soon" badge + disabled
  //                   CTA. Reveals feature roadmap without teasing paid
  //                   users who'd expect access.
  //   • locked      → module active for this grade, but user's plan
  //                   doesn't include it → "Upgrade to Access" CTA.
  //   • active      → normal "View Upcoming Classes" CTA.
  const liveClassesGradeStatus = userGrade && getLiveClassesStatusForGrade
    ? getLiveClassesStatusForGrade(userGrade)
    : null;
  const liveClassesParentStatus = getModuleStatus ? getModuleStatus('live_classes') : null;
  const liveClassesStatus = liveClassesGradeStatus ?? liveClassesParentStatus;
  const liveClassesHidden = liveClassesStatus === 'hidden';
  const liveClassesComingSoon = liveClassesStatus === 'coming_soon';
  const showLiveClassesCard = isLoggedIn && !liveClassesHidden;
  const liveClassesLocked =
    isLoggedIn && !liveClassesComingSoon && !canAccessLiveClasses();
  
  // ===========================================
  // SIMPLIFIED VISIBILITY/STATUS LOGIC:
  // - Guests: Only check VISIBILITY (global setting)
  // - Logged-in Users: Only check their grade's STATUS
  // ===========================================
  
  // --- VISIBILITY (for guests only) ---
  // Check visibility using sample modules (visibility is global per type)
  const isOralVisibleForGuests = isModuleVisible('meo_class_1_oral');
  const isWrittenVisibleForGuests = isModuleVisible('meo_class_1_written');
  const isPracticeVisibleForGuests = isModuleVisible('practice_sessions');
  const isProgressVisibleForGuests = isModuleVisible('progress_tracker');
  const isNotebookVisibleForGuests = isModuleVisible('notebook');
  const isInsightsVisibleForGuests = isModuleVisible('exam_insights');
  
  // --- STATUS (for logged-in users only) ---
  // Get status for user's selected grade
  const oralStatus = userGrade ? getOralStatus(userGrade) : 'active';
  const writtenStatus = userGrade ? getWrittenStatus(userGrade) : 'active';
  const practiceStatus = getPracticeSessionsStatus();
  const progressStatus = getProgressTrackerStatus();
  const notebookStatus = userGrade ? getNotebookStatusForGrade(userGrade) : 'active';
  const insightsStatus = userGrade ? getInsightsStatusForGrade(userGrade) : 'active';
  
  // --- ACTIVE STATE (for button enabled/disabled) ---
  const oralActive = userGrade ? isOralActive(userGrade) : true;
  const writtenActive = userGrade ? isWrittenActive(userGrade) : true;
  const practiceActive = isPracticeSessionsActive();
  const progressActive = isProgressTrackerActive();
  
  // --- SHOULD SHOW CARD? ---
  // Guests: Show if visible (visibility = 'everyone')
  // Logged-in: Show if status is not 'disabled' or 'hidden'
  const shouldShowOral = isLoggedIn 
    ? (oralStatus !== 'disabled' && oralStatus !== 'hidden')
    : isOralVisibleForGuests;
  
  const shouldShowWritten = isLoggedIn 
    ? (writtenStatus !== 'disabled' && writtenStatus !== 'hidden')
    : isWrittenVisibleForGuests;
  
  const shouldShowPractice = isLoggedIn 
    ? (practiceStatus !== 'disabled' && practiceStatus !== 'hidden')
    : isPracticeVisibleForGuests;
  
  const shouldShowProgress = isLoggedIn 
    ? (progressStatus !== 'disabled' && progressStatus !== 'hidden')
    : isProgressVisibleForGuests;
  
  const shouldShowNotebook = isLoggedIn 
    ? (notebookStatus !== 'disabled' && notebookStatus !== 'hidden')
    : isNotebookVisibleForGuests;
  
  const shouldShowInsights = isLoggedIn 
    ? (insightsStatus !== 'disabled' && insightsStatus !== 'hidden')
    : isInsightsVisibleForGuests;

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/progress/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTransparencyStats = async () => {
    if (!preferences.grade || !preferences.mmd_location) return;
    try {
      const response = await axios.get(`${API}/user/transparency-stats`, {
        params: {
          grade: preferences.grade,
          mmd_location: preferences.mmd_location
        }
      });
      setTransparencyStats(response.data);
    } catch (error) {
      console.error('Failed to fetch transparency stats:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      fetchStats();
    }
  }, []);

  // Fetch transparency stats when preferences change
  useEffect(() => {
    if (preferences.grade && preferences.mmd_location) {
      fetchTransparencyStats();
    }
  }, [preferences.grade, preferences.mmd_location]);

  const handleNavigate = (path) => {
    // Allow Question Bank for guests, but other features require login
    if (!isAuthenticated() && path !== '/question-bank') {
      toast.error('Please login to access this feature');
      setShowAuth(true);
      return;
    }
    
    if (!preferences.grade || !preferences.mmd_location) {
      toast.error('Please set your grade and MMD location in your profile');
      navigate('/profile');
      return;
    }
    
    navigate(path);
  };

  return (
    <Layout>
      <div className="page-transition max-w-6xl mx-auto">
        {/* Community banner — appears only if admin enabled it AND the
            account is older than the configured delay AND the user has
            not dismissed it on this device. */}
        <CommunityBanner />

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome, {user ? user.name : 'Guest'}!
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {preferences.grade && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    <span>{preferences.grade}</span>
                  </div>
                )}
                {preferences.mmd_location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{preferences.mmd_location}</span>
                  </div>
                )}
              </div>
            </div>
            
            {!isAuthenticated() ? (
              <Button
                onClick={() => setShowAuth(true)}
                className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
              >
                Login to Get Started
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/submit')}
                className="bg-[#14B8A6] hover:bg-[#14B8A6]/90 flex items-center gap-2"
              >
                <FileEdit className="w-4 h-4" />
                Submit Question
              </Button>
            )}
          </div>
        </div>

        {/* Main Module Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Oral Question Bank Card */}
          {shouldShowOral && (
            <Card className={`hover:shadow-lg transition-shadow border-t-4 border-t-[#0A7EA4] ${!oralActive ? 'opacity-75' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#0A7EA4]/10 rounded-lg">
                    <BookOpen className="w-6 h-6 text-[#0A7EA4]" />
                  </div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    Oral Question Bank
                    {oralStatus === 'coming_soon' && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">Coming Soon</Badge>
                    )}
                  </CardTitle>
                </div>
                <CardDescription>
                  Browse and study all oral questions filtered by your grade and MMD
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0A7EA4] rounded-full"></div>
                    Filter by function, topic, surveyor
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0A7EA4] rounded-full"></div>
                    Mark questions as complete
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0A7EA4] rounded-full"></div>
                    Track your study progress
                  </li>
                </ul>
                {oralActive ? (
                  <Button
                    onClick={() => handleNavigate('/question-bank')}
                    className="w-full bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                  >
                    Browse Questions
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Written Question Bank Card */}
          {shouldShowWritten && (
            <Card className={`hover:shadow-lg transition-shadow border-t-4 border-t-[#EC4899] ${!writtenActive ? 'opacity-75' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#EC4899]/10 rounded-lg">
                    <PenTool className="w-6 h-6 text-[#EC4899]" />
                  </div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    Written Question Bank
                    {writtenStatus === 'coming_soon' && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">Coming Soon</Badge>
                    )}
                  </CardTitle>
                </div>
                <CardDescription>
                  Browse written exam papers with questions and answers by grade
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#EC4899] rounded-full"></div>
                    View complete exam papers
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#EC4899] rounded-full"></div>
                    Filter by grade and function
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#EC4899] rounded-full"></div>
                    Show/hide answers for practice
                  </li>
                </ul>
                {writtenActive ? (
                  <Button
                    onClick={() => handleNavigate('/written-bank')}
                    className="w-full bg-[#EC4899] hover:bg-[#EC4899]/90"
                  >
                    Browse Written Papers
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Practice Sessions Card */}
          {shouldShowPractice && (
            <Card className={`hover:shadow-lg transition-shadow border-t-4 border-t-[#8B5CF6] ${!practiceActive ? 'opacity-75' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#8B5CF6]/10 rounded-lg">
                    <PlayCircle className="w-6 h-6 text-[#8B5CF6]" />
                  </div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    Practice Sessions
                    {practiceStatus === 'coming_soon' && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">Coming Soon</Badge>
                    )}
                  </CardTitle>
                </div>
                <CardDescription>
                  Take mock tests with self-assessment to evaluate your preparation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full"></div>
                    Configure custom practice tests
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full"></div>
                    Self-assess your answers
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full"></div>
                    Get instant scores and feedback
                  </li>
                </ul>
                {practiceActive ? (
                  <Button
                    onClick={() => handleNavigate('/practice')}
                    className="w-full bg-[#8B5CF6] hover:bg-[#8B5CF6]/90"
                  >
                    Start Practice
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Progress Tracker Card */}
          {shouldShowProgress && (
            <Card className={`hover:shadow-lg transition-shadow border-t-4 border-t-[#10B981] ${!progressActive ? 'opacity-75' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#10B981]/10 rounded-lg">
                    <Target className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    Progress Tracker
                    {progressStatus === 'coming_soon' && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">Coming Soon</Badge>
                    )}
                  </CardTitle>
                </div>
                <CardDescription>
                  Track your preparation progress for oral and written exams
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                    Set exam dates for oral &amp; written functions
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                    View detailed analytics
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                    Monitor completion rates
                  </li>
                </ul>
                {progressActive ? (
                  <Button
                    onClick={() => handleNavigate('/progress')}
                    className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                  >
                    View Progress
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Study Notebook Card */}
          {shouldShowNotebook && (
            <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#14B8A6]" data-testid="notebook-card">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#14B8A6]/10 rounded-lg">
                    <Notebook className="w-6 h-6 text-[#14B8A6]" />
                  </div>
                  <CardTitle className="text-xl">Study Notebook</CardTitle>
                  {notebookStatus === 'coming_soon' && (
                    <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 bg-amber-50">
                      Coming Soon
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  Comprehensive study notes organized by function and topic for your grade
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#14B8A6] rounded-full"></div>
                    Read chapter-wise study material
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#14B8A6] rounded-full"></div>
                    Bookmark important sections
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#14B8A6] rounded-full"></div>
                    View related exam questions
                  </li>
                </ul>
                {notebookStatus === 'coming_soon' ? (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleNavigate('/notebook')}
                    className="w-full bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                  >
                    Open Notebook
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Exam Insights Card */}
          {shouldShowInsights && (
            <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#0891B2]" data-testid="insights-card">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#0891B2]/10 rounded-lg">
                    <Lightbulb className="w-6 h-6 text-[#0891B2]" />
                  </div>
                  <CardTitle className="text-xl">Exam Insights</CardTitle>
                  {insightsStatus === 'coming_soon' && (
                    <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 bg-amber-50">
                      Coming Soon
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  Data-driven intelligence for exam preparation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0891B2] rounded-full"></div>
                    Topic-wise question distribution
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0891B2] rounded-full"></div>
                    Surveyor patterns &amp; preferences
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0891B2] rounded-full"></div>
                    Hot topics at your MMD
                  </li>
                </ul>
                {insightsStatus === 'coming_soon' ? (
                  <Button disabled className="w-full bg-gray-300 cursor-not-allowed">
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleNavigate('/insights')}
                    className="w-full bg-[#0891B2] hover:bg-[#0891B2]/90"
                  >
                    View Insights
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Live Classes Card (Tier 2 module) */}
          {showLiveClassesCard && (
            <Card
              className={`hover:shadow-lg transition-shadow border-t-4 border-t-[#FF6B6B] ${liveClassesLocked || liveClassesComingSoon ? 'opacity-95' : ''}`}
              data-testid="live-classes-card"
            >
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[#FF6B6B]/10 rounded-lg">
                    <Video className="w-6 h-6 text-[#FF6B6B]" />
                  </div>
                  <CardTitle className="text-xl">Live Classes</CardTitle>
                  {liveClassesComingSoon ? (
                    <Badge className="ml-2 bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100 gap-1">
                      <Lock className="w-3 h-3" />
                      Coming Soon
                    </Badge>
                  ) : liveClassesLocked ? (
                    <Badge className="ml-2 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-100 gap-1">
                      <Lock className="w-3 h-3" />
                      Premium
                    </Badge>
                  ) : null}
                </div>
                <CardDescription>
                  Instructor-led sessions with real-time Q&amp;A
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></div>
                    Live Discussions and Doubt Clearing Sessions
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></div>
                    Grade-specific sessions from experienced hosts
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></div>
                    Join right from your browser &mdash; no downloads
                  </li>
                </ul>
                {liveClassesComingSoon ? (
                  <Button
                    disabled
                    className="w-full bg-gray-300 cursor-not-allowed"
                    data-testid="live-classes-card-coming-soon-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                ) : liveClassesLocked ? (
                  <>
                    <Button
                      onClick={() => handleNavigate('/plans?highlight=live_classes')}
                      className="w-full bg-[#FF6B6B] hover:bg-[#FF5555] text-white gap-2"
                      data-testid="live-classes-card-upgrade-cta"
                    >
                      <Lock className="w-4 h-4" />
                      Upgrade to Access
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Included in plans marked with the &ldquo;Live Classes&rdquo; badge
                    </p>
                  </>
                ) : (
                  <Button
                    onClick={() => handleNavigate('/live-classes')}
                    className="w-full bg-[#FF6B6B] hover:bg-[#FF5555] text-white"
                    data-testid="live-classes-card-cta"
                  >
                    View Upcoming Classes
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Class Archive Card (Tier 2 module — same gating as Live Classes) */}
          {showLiveClassesCard && (
            <Card
              className={`hover:shadow-lg transition-shadow border-t-4 border-t-emerald-500 ${liveClassesLocked || liveClassesComingSoon ? 'opacity-95' : ''}`}
              data-testid="class-archive-card"
            >
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Archive className="w-6 h-6 text-emerald-700" />
                  </div>
                  <CardTitle className="text-xl">Class Archive</CardTitle>
                  {liveClassesComingSoon ? (
                    <Badge className="ml-2 bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100 gap-1">
                      <Lock className="w-3 h-3" />
                      Coming Soon
                    </Badge>
                  ) : liveClassesLocked ? (
                    <Badge className="ml-2 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-100 gap-1">
                      <Lock className="w-3 h-3" />
                      Premium
                    </Badge>
                  ) : null}
                </div>
                <CardDescription>
                  Replay any past session with recording, summary and Q&amp;A
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                    Full recordings of every past class in your grade
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                    Instructor summaries and complete Q&amp;A archive
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                    Search by topic, question, or function &mdash; revise on your time
                  </li>
                </ul>
                {liveClassesComingSoon ? (
                  <Button
                    disabled
                    className="w-full bg-gray-300 cursor-not-allowed"
                    data-testid="class-archive-card-coming-soon-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Coming Soon
                  </Button>
                ) : liveClassesLocked ? (
                  <Button
                    onClick={() => handleNavigate('/plans?highlight=live_classes')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    data-testid="class-archive-card-upgrade-cta"
                  >
                    <Lock className="w-4 h-4" />
                    Upgrade to Access
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleNavigate('/live-classes/archive')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="class-archive-card-cta"
                  >
                    Browse Archive
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Transparency Corner */}
        {transparencyStats && preferences.grade && preferences.mmd_location && (
          <Card className="bg-gradient-to-r from-[#0A7EA4]/5 to-[#14B8A6]/5 border-[#0A7EA4]/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="w-5 h-5 text-[#0A7EA4]" />
                Transparency Corner
              </CardTitle>
              <CardDescription className="text-sm">
                {preferences.grade} • {preferences.mmd_location}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="oral" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#0A7EA4]/10">
                  <TabsTrigger 
                    value="oral" 
                    className="data-[state=active]:bg-[#0A7EA4] data-[state=active]:text-white data-[state=inactive]:text-[#0A7EA4] data-[state=inactive]:hover:bg-[#0A7EA4]/20"
                  >
                    Oral
                  </TabsTrigger>
                  <TabsTrigger 
                    value="written"
                    className="data-[state=active]:bg-[#0A7EA4] data-[state=active]:text-white data-[state=inactive]:text-[#0A7EA4] data-[state=inactive]:hover:bg-[#0A7EA4]/20"
                  >
                    Written
                  </TabsTrigger>
                </TabsList>
                
                {/* Oral Tab */}
                <TabsContent value="oral">
                  {transparencyStats.oral?.by_function?.length > 0 ? (
                    <>
                      {/* Summary row */}
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-200">
                        <span className="font-medium text-gray-700">All Functions</span>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <span className="text-xl font-bold text-[#0A7EA4]">{transparencyStats.oral?.total_questions || 0}</span>
                            <span className="text-xs text-gray-500 ml-1">Total</span>
                          </div>
                          <div className="text-center">
                            <span className="text-xl font-bold text-[#10B981]">{transparencyStats.oral?.answered_questions || 0}</span>
                            <span className="text-xs text-gray-500 ml-1">Answered</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Function-wise breakdown */}
                      <div className="space-y-2">
                        {transparencyStats.oral?.by_function?.map((func) => (
                          <div key={func.function_id} className="flex items-start justify-between gap-3 py-2 hover:bg-white/50 rounded px-2 -mx-2">
                            <span className="text-sm text-gray-700 break-words min-w-0 flex-1">{func.function_name}</span>
                            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                              <div className="w-12 sm:w-16 text-right">
                                <span className="text-sm font-semibold text-[#0A7EA4]">{func.total_questions}</span>
                              </div>
                              <div className="w-12 sm:w-16 text-right">
                                <span className="text-sm font-semibold text-[#10B981]">{func.answered_questions}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {transparencyStats.oral?.total_questions > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-200 text-center">
                          <div className="text-sm text-gray-500">
                            <span className="font-medium text-[#10B981]">
                              {Math.round((transparencyStats.oral?.answered_questions / transparencyStats.oral?.total_questions) * 100)}%
                            </span>
                            {' '}of questions have answers
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <p className="text-sm">No oral questions available for this grade/location.</p>
                    </div>
                  )}
                </TabsContent>
                
                {/* Written Tab */}
                <TabsContent value="written">
                  {transparencyStats.written?.by_function?.length > 0 ? (
                    <>
                      {/* Summary row */}
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-200">
                        <span className="font-medium text-gray-700">All Subjects</span>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <span className="text-xl font-bold text-[#0A7EA4]">{transparencyStats.written?.total_questions || 0}</span>
                            <span className="text-xs text-gray-500 ml-1">Total</span>
                          </div>
                          <div className="text-center">
                            <span className="text-xl font-bold text-[#10B981]">{transparencyStats.written?.answered_questions || 0}</span>
                            <span className="text-xs text-gray-500 ml-1">Answered</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Subject-wise breakdown */}
                      <div className="space-y-2">
                        {transparencyStats.written?.by_function?.map((func) => (
                          <div key={func.function_id} className="flex items-start justify-between gap-3 py-2 hover:bg-white/50 rounded px-2 -mx-2">
                            <span className="text-sm text-gray-700 break-words min-w-0 flex-1">{func.function_name}</span>
                            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                              <div className="w-12 sm:w-16 text-right">
                                <span className="text-sm font-semibold text-[#0A7EA4]">{func.total_questions}</span>
                              </div>
                              <div className="w-12 sm:w-16 text-right">
                                <span className="text-sm font-semibold text-[#10B981]">{func.answered_questions}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {transparencyStats.written?.total_questions > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-200 text-center">
                          <div className="text-sm text-gray-500">
                            <span className="font-medium text-[#10B981]">
                              {Math.round((transparencyStats.written?.answered_questions / transparencyStats.written?.total_questions) * 100)}%
                            </span>
                            {' '}of questions have answers
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <p className="text-sm">No written questions available for this grade.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </Layout>
  );
};

export default DashboardPage;
